// The leadership dashboard's figures (P5.2, screen 19; 01-SPEC §7, D28, D67), worked out in the
// browser from the nightly snapshot (D66), so changing a filter needs no new request. Pure and
// unit-tested. Amounts are minor units in their own currency until shown: they are converted to
// SAR at the month's rate (ExchangeRates, reporting only); a month without a rate uses the latest
// earlier rate, and an amount with no rate at all is left out and named.

import {
  STAGES,
  type Dims,
  type ExceptionItem,
  type ExceptionKind,
  type MonthFact,
  type PipelineItem,
  type Snapshot,
  type Stage,
  type SupplierFact,
} from './facts';

export interface Rate {
  /** YYYY-MM. */
  month: string;
  currency: string;
  rateToSar: number;
}

export interface DashboardBody {
  year: number;
  /** Department heads see their own departments only (D28). */
  scope: { all: true } | { all: false; departments: string[] };
  current: Snapshot | null;
  /** Last year's monthly facts, for "vs last year". */
  previous: { builtAt: string; months: MonthFact[] } | null;
  rates: Rate[];
  categories: { id: string; nameEN: string; nameAR: string }[];
}

export const PERIODS = ['ytd', 'last3', 'month', 'year'] as const;
export type Period = (typeof PERIODS)[number];
export const VIEWS = ['department', 'vertical', 'office'] as const;
export type ViewBy = (typeof VIEWS)[number];

export interface Filters {
  period: Period;
  /** '' = all. */
  entity: string;
  vertical: string;
  office: string;
  department: string;
}

export const ALL_FILTERS: Filters = {
  period: 'ytd',
  entity: '',
  vertical: '',
  office: '',
  department: '',
};

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * The months a period covers within the year, given today's month (YYYY-MM, Riyadh). A past year
 * counts as complete: year to date is the whole year, "this month" is December.
 */
export function periodMonths(period: Period, year: number, today: string): string[] {
  const todayYear = Number(today.slice(0, 4));
  const last = year < todayYear ? 12 : year > todayYear ? 0 : Number(today.slice(5, 7));
  const all = Array.from({ length: 12 }, (_, i) => `${year}-${pad(i + 1)}`);
  if (period === 'year') return all;
  if (period === 'month') return last ? [all[last - 1] as string] : [];
  if (period === 'last3') return all.slice(Math.max(0, last - 3), last);
  return all.slice(0, last);
}

/** Rate to SAR for a currency in a month: that month's, else the latest earlier one; null if none. */
export function rateFor(rates: readonly Rate[], currency: string, month: string): number | null {
  if (!currency || currency === 'SAR') return 1;
  let best: Rate | null = null;
  for (const r of rates) {
    if (r.currency !== currency || r.month > month || !(r.rateToSar > 0)) continue;
    if (!best || r.month > best.month) best = r;
  }
  return best ? best.rateToSar : null;
}

/** Converts minor units to SAR minor units; records the currency when no rate is known. */
export function toSar(
  minor: number,
  currency: string,
  month: string,
  rates: readonly Rate[],
  missing: Set<string>,
): number {
  if (!minor) return 0;
  const rate = rateFor(rates, currency, month);
  if (rate === null) {
    missing.add(currency);
    return 0;
  }
  return Math.round(minor * rate);
}

export function matches(d: Dims, f: Filters): boolean {
  return (
    (!f.entity || d.entity === f.entity) &&
    (!f.vertical || d.vertical === f.vertical) &&
    (!f.office || d.office === f.office) &&
    (!f.department || d.department === f.department)
  );
}

const pct = (now: number, before: number): number | null =>
  before > 0 ? Math.round(((now - before) / before) * 100) : null;

export interface Headline {
  prCount: number;
  prValue: number;
  poCount: number;
  poValue: number;
  delivered: number;
  /** Delivered as a share of PO value, 0–100. */
  deliveredPct: number;
  pipelineCount: number;
  pipelineValue: number;
  /** Average days from request to PO; null when no PO in the period. */
  prToPoDays: number | null;
  pettyCash: number;
  vsLastYear: { prValue: number | null; poValue: number | null; pettyCash: number | null };
  /** Currencies left out of SAR totals for want of a rate. */
  missingRates: string[];
}

interface Sums {
  prCount: number;
  prValue: number;
  poCount: number;
  poValue: number;
  delivered: number;
  paid: number;
  pettyCash: number;
  days: number;
  daysN: number;
}

function sum(
  months: readonly MonthFact[],
  covered: ReadonlySet<string>,
  f: Filters,
  rates: readonly Rate[],
  missing: Set<string>,
): Sums {
  const s: Sums = {
    prCount: 0,
    prValue: 0,
    poCount: 0,
    poValue: 0,
    delivered: 0,
    paid: 0,
    pettyCash: 0,
    days: 0,
    daysN: 0,
  };
  for (const m of months) {
    if (!covered.has(m.month) || !matches(m, f)) continue;
    const sar = (v: number) => toSar(v, m.currency, m.month, rates, missing);
    s.prCount += m.prCount;
    s.prValue += sar(m.prValue);
    s.poCount += m.poCount;
    s.poValue += sar(m.poValue);
    s.delivered += sar(m.deliveredValue);
    s.paid += sar(m.paidValue);
    s.pettyCash += sar(m.pettyCash);
    s.days += m.prToPoDays;
    s.daysN += m.prToPoCount;
  }
  return s;
}

/** The six headline tiles of screen 19, with "vs last year" for the same months. */
export function headline(body: DashboardBody, f: Filters, today: string): Headline {
  const missing = new Set<string>();
  const months = periodMonths(f.period, body.year, today);
  const now = sum(body.current?.months ?? [], new Set(months), f, body.rates, missing);
  const lastYearMonths = new Set(months.map((m) => `${Number(m.slice(0, 4)) - 1}${m.slice(4)}`));
  const before = sum(body.previous?.months ?? [], lastYearMonths, f, body.rates, new Set());
  const currentMonth = months.at(-1) ?? `${body.year}-12`;
  let pipelineCount = 0;
  let pipelineValue = 0;
  for (const p of body.current?.pipeline ?? []) {
    if (!matches(p, f)) continue;
    pipelineCount += 1;
    pipelineValue += toSar(p.value, p.currency, currentMonth, body.rates, missing);
  }
  return {
    prCount: now.prCount,
    prValue: now.prValue,
    poCount: now.poCount,
    poValue: now.poValue,
    delivered: now.delivered,
    deliveredPct: now.poValue > 0 ? Math.round((now.delivered / now.poValue) * 100) : 0,
    pipelineCount,
    pipelineValue,
    prToPoDays: now.daysN > 0 ? Math.round((now.days / now.daysN) * 10) / 10 : null,
    pettyCash: now.pettyCash,
    vsLastYear: body.previous
      ? {
          prValue: pct(now.prValue, before.prValue),
          poValue: pct(now.poValue, before.poValue),
          pettyCash: pct(now.pettyCash, before.pettyCash),
        }
      : { prValue: null, poValue: null, pettyCash: null },
    missingRates: [...missing].sort(),
  };
}

export const ENTITY_ORDER = ['SMC', 'VIB', 'MED'] as const;

/** PO value issued per month and entity (SAR), for the stacked chart: the period's months. */
export function poByEntity(
  body: DashboardBody,
  f: Filters,
  today: string,
): { month: string; values: Record<string, number>; total: number }[] {
  const months = periodMonths(f.period === 'month' ? 'last3' : f.period, body.year, today);
  const missing = new Set<string>();
  return months.map((month) => {
    const values: Record<string, number> = {};
    for (const m of body.current?.months ?? []) {
      if (m.month !== month || !matches(m, f) || !m.poValue) continue;
      values[m.entity] =
        (values[m.entity] ?? 0) + toSar(m.poValue, m.currency, m.month, body.rates, missing);
    }
    return { month, values, total: Object.values(values).reduce((a, b) => a + b, 0) };
  });
}

/** Open requests by stage now, with the average days they have been in it. */
export function pipelineByStage(
  body: DashboardBody,
  f: Filters,
): { stage: Stage; count: number; avgDays: number }[] {
  const items = (body.current?.pipeline ?? []).filter((p: PipelineItem) => matches(p, f));
  return STAGES.map((stage) => {
    const here = items.filter((p) => p.stage === stage);
    const avg = here.length ? here.reduce((a, p) => a + p.daysInStage, 0) / here.length : 0;
    return { stage, count: here.length, avgDays: Math.round(avg * 10) / 10 };
  });
}

/** The top PO value (SAR) by department, vertical, office or category in the period. */
export function topBy(
  body: DashboardBody,
  f: Filters,
  today: string,
  by: ViewBy | 'category',
  limit = 6,
): { key: string; value: number }[] {
  const months = new Set(periodMonths(f.period, body.year, today));
  const totals = new Map<string, number>();
  const missing = new Set<string>();
  for (const m of body.current?.months ?? []) {
    if (!months.has(m.month) || !matches(m, f) || !m.poValue) continue;
    const k = m[by] || '—';
    totals.set(
      k,
      (totals.get(k) ?? 0) + toSar(m.poValue, m.currency, m.month, body.rates, missing),
    );
  }
  return [...totals.entries()]
    .map(([key, value]) => ({ key, value }))
    .filter((r) => r.value > 0)
    .sort((a, b) => b.value - a.value || a.key.localeCompare(b.key))
    .slice(0, limit);
}

/** The top suppliers by PO value (SAR) in the period, with how many POs. */
export function topSuppliers(
  body: DashboardBody,
  f: Filters,
  today: string,
  limit = 6,
): { supplierId: string; name: string; value: number; count: number }[] {
  const months = new Set(periodMonths(f.period, body.year, today));
  const totals = new Map<string, { name: string; value: number; count: number }>();
  const missing = new Set<string>();
  for (const s of body.current?.suppliers ?? ([] as SupplierFact[])) {
    if (!months.has(s.month) || !matches(s, f)) continue;
    const t = totals.get(s.supplierId) ?? { name: s.supplierName, value: 0, count: 0 };
    t.value += toSar(s.poValue, s.currency, s.month, body.rates, missing);
    t.count += s.poCount;
    totals.set(s.supplierId, t);
  }
  return [...totals.entries()]
    .map(([supplierId, t]) => ({ supplierId, ...t }))
    .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name))
    .slice(0, limit);
}

/** The exceptions in the period, grouped by kind (in the order 01-SPEC §7 lists them). */
export function exceptionsByKind(
  body: DashboardBody,
  f: Filters,
  today: string,
): { kind: ExceptionKind; items: ExceptionItem[] }[] {
  const months = new Set(periodMonths(f.period, body.year, today));
  const items = (body.current?.exceptions ?? []).filter(
    (e) => months.has(e.month) && matches(e, f),
  );
  const kinds: ExceptionKind[] = [
    'rushFewQuotes',
    'cfoOwnRequest',
    'sentToHead',
    'priceVariance',
    'sentBeforeCfo',
    'voucherReturned',
  ];
  return kinds.map((kind) => ({ kind, items: items.filter((e) => e.kind === kind) }));
}

/** The values the filters can take, from the data the viewer may see. */
export function filterOptions(body: DashboardBody): {
  entities: string[];
  verticals: string[];
  offices: string[];
  departments: string[];
} {
  const rows: Dims[] = [...(body.current?.months ?? []), ...(body.current?.pipeline ?? [])];
  const uniq = (k: keyof Dims) =>
    [...new Set(rows.map((r) => r[k]).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  return {
    entities: uniq('entity'),
    verticals: uniq('vertical'),
    offices: uniq('office'),
    departments: uniq('department'),
  };
}

const CSV_HEAD = [
  'Month',
  'Entity',
  'Vertical',
  'Office',
  'Department',
  'Category',
  'Currency',
  'Requests',
  'Requested (estimate)',
  'POs',
  'PO value',
  'Delivered',
  'Paid',
  'Petty cash',
  'PO value (SAR)',
];

const cell = (v: string | number) => {
  const s = String(v);
  // Excel would run a cell starting with = + - @ as a formula.
  const safe = /^[=+\-@]/.test(s) ? `'${s}` : s;
  return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};
const major = (minor: number) => (minor / 100).toFixed(2);
/** Byte order mark: Excel then reads the file as UTF-8 (Arabic names). */
const BOM = String.fromCharCode(0xfeff);

/** The filtered monthly facts as CSV (UTF-8 with BOM, so Excel shows Arabic correctly). */
export function exportCsv(body: DashboardBody, f: Filters, today: string): string {
  const months = new Set(periodMonths(f.period, body.year, today));
  const missing = new Set<string>();
  const lines = [CSV_HEAD.join(',')];
  for (const m of body.current?.months ?? []) {
    if (!months.has(m.month) || !matches(m, f)) continue;
    lines.push(
      [
        m.month,
        m.entity,
        m.vertical,
        m.office,
        m.department,
        m.category,
        m.currency,
        m.prCount,
        major(m.prValue),
        m.poCount,
        major(m.poValue),
        major(m.deliveredValue),
        major(m.paidValue),
        major(m.pettyCash),
        rateFor(body.rates, m.currency, m.month) === null
          ? ''
          : major(toSar(m.poValue, m.currency, m.month, body.rates, missing)),
      ]
        .map(cell)
        .join(','),
    );
  }
  return `${BOM}${lines.join('\r\n')}\r\n`;
}
